// src/features/streams/components/schedule/StreamScheduleSection.tsx

import { useMemo, useState } from 'react';
import { Card, Stack, Group, Text, Button, Select, Alert, Collapse, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconAlertTriangle, IconInfoCircle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import type { IMediaStreamScheduleAllData } from '@lib2/model/stream/MediaStreamScheduleTypes';
import type { StreamViewData } from '@/lib/types/Stream';
import type { StreamRecordMode } from '@/lib/constants/stream';
import { useUpdateStreamSettings } from '../../useStreamMutations';
import { StreamUtils } from '../../StreamUtils';
import { StreamScheduleUtils } from '../../StreamScheduleUtils';
import type { ScheduleFormData } from '../../schemas/ScheduleForm.schema';
import { ScheduleEditorModal } from './ScheduleEditorModal';
import { ScheduleListItem } from './ScheduleListItem';

type ScheduleModalState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; original: IMediaStreamScheduleAllData };

interface StreamScheduleSectionProps {
  stream: StreamViewData;
  /** Current (possibly unsaved) Overview form record mode */
  formRecordMode: StreamRecordMode;
}

/** IANA zone options; falls back to the browser zone if the Intl API is missing. */
function getZoneOptions(currentZone: string): string[] {
  const zones: string[] =
    typeof Intl.supportedValuesOf === 'function'
      ? [...Intl.supportedValuesOf('timeZone')]
      : [DateTime.local().zoneName];
  // A persisted legacy zone alias (e.g. 'US/Central') may not be in the list — keep it selectable
  if (!zones.includes(currentZone)) zones.unshift(currentZone);
  return zones;
}

export function StreamScheduleSection({ stream, formRecordMode }: StreamScheduleSectionProps) {
  const mediaId = stream.media.mediaId;
  const updateStream = useUpdateStreamSettings(mediaId);

  const savedRecordMode = stream.stream?.record?.recordMode ?? 'manual';
  const isActive = savedRecordMode === 'schedule';
  const pendingSave = formRecordMode === 'schedule' && !isActive;
  const switchingAway = isActive && formRecordMode !== 'schedule';

  const zoneId = stream.stream?.core?.scheduleZoneId ?? DateTime.local().zoneName;
  const zoneOptions = useMemo(() => getZoneOptions(zoneId), [zoneId]);

  // Recomputed when server state changes (every save refreshes the stream object)
  const nowSecs = useMemo(() => Math.floor(Date.now() / 1000), [stream]);
  const { active, expired, other } = useMemo(
    () => StreamScheduleUtils.partitionSchedules(stream.schedules ?? [], zoneId, nowSecs),
    [stream.schedules, zoneId, nowSecs],
  );
  const hasRecordSchedules = active.length + expired.length > 0;

  const [modalState, setModalState] = useState<ScheduleModalState>({ mode: 'closed' });
  const [rootError, setRootError] = useState<string | null>(null);
  const [expiredOpen, { toggle: toggleExpired }] = useDisclosure(false);

  // Fixed per modal open: schema refinements + occurrence previews + default form values
  const modalNowSecs = useMemo(() => Math.floor(Date.now() / 1000), [modalState]);
  const modalDefaults = useMemo<ScheduleFormData>(() => {
    if (modalState.mode === 'edit') {
      return StreamScheduleUtils.scheduleToFormData(modalState.original, zoneId);
    }
    return StreamScheduleUtils.createDefaultFormData(zoneId, modalNowSecs);
  }, [modalState, zoneId, modalNowSecs]);

  /** Full-array replace: record schedules from the args + non-record pass-through. */
  const saveSchedules = async (
    nextRecordSchedules: IMediaStreamScheduleAllData[],
    nextZoneId: string,
  ) => {
    const req = StreamUtils.transformSchedulesToStreamUpdateRequest(
      stream,
      [...nextRecordSchedules, ...other],
      nextZoneId,
    );
    await updateStream.mutateAsync(req);
  };

  const closeModal = () => {
    setModalState({ mode: 'closed' });
    setRootError(null);
  };

  const handleModalSubmit = async (data: ScheduleFormData, extraWeekdays: number[]) => {
    const submitNow = Math.floor(Date.now() / 1000);
    const original = modalState.mode === 'edit' ? modalState.original : undefined;
    const base = StreamScheduleUtils.formDataToSchedule(data, zoneId, mediaId, submitNow, original);

    // Multi-day weekly create: one weekly schedule per selected weekday, all
    // sharing the base wall-clock window. Each candidate is checked against
    // existing schedules AND the candidates accepted before it — the backend
    // does no overlap validation.
    const candidates = [
      base,
      ...extraWeekdays.map((day) => StreamScheduleUtils.shiftToWeekday(base, day, zoneId)),
    ];

    const accepted: IMediaStreamScheduleAllData[] = [];
    for (const candidate of candidates) {
      const conflict = StreamScheduleUtils.findConflict(
        candidate,
        [...active, ...accepted],
        zoneId,
        submitNow,
        original,
      );
      if (conflict) {
        const label = StreamScheduleUtils.getFrequencyLabel(conflict.schedule.interval, conflict.schedule.intervalType);
        const window = StreamScheduleUtils.formatScheduleWindow(conflict.schedule, zoneId);
        setRootError(`Overlaps with an existing schedule: ${label} · ${window}`);
        return;
      }
      accepted.push(candidate);
    }

    const nextRecord = original
      ? [
          ...active.map((s) => (s.schedule.scheduleId === original.schedule.scheduleId ? base : s)),
          ...expired,
        ]
      : [...active, ...expired, ...accepted];

    try {
      await saveSchedules(nextRecord, zoneId);
      notifications.show({
        title: 'Saved',
        message: original
          ? 'Recording schedule updated.'
          : accepted.length > 1
            ? `${accepted.length} weekly recording schedules added.`
            : 'Recording schedule added.',
        color: 'green',
      });
      closeModal();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save schedule',
        color: 'red',
      });
    }
  };

  const handleDelete = (scheduleAll: IMediaStreamScheduleAllData) => {
    const s = scheduleAll.schedule;
    const label = StreamScheduleUtils.getFrequencyLabel(s.interval, s.intervalType);
    const window = StreamScheduleUtils.formatScheduleWindow(s, zoneId);
    modals.openConfirmModal({
      title: 'Delete recording schedule?',
      children: (
        <Text size="sm">
          {label} · {window}
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const nextRecord = [...active, ...expired].filter(
          (x) => x.schedule.scheduleId !== s.scheduleId,
        );
        try {
          await saveSchedules(nextRecord, zoneId);
          notifications.show({ title: 'Deleted', message: 'Recording schedule deleted.', color: 'green' });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to delete schedule',
            color: 'red',
          });
        }
      },
    });
  };

  const handleZoneChange = async (newZone: string | null) => {
    if (!newZone || newZone === zoneId) return;
    try {
      await saveSchedules([...active, ...expired], newZone);
      notifications.show({ title: 'Saved', message: `Schedule timezone set to ${newZone}.`, color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to update timezone',
        color: 'red',
      });
    }
  };

  return (
    <Card shadow="sm" padding="lg" withBorder>
      <Group justify="space-between" mb="md">
        <Text fw={600}>Recording Schedule</Text>
        {isActive && (
          // type="button": this card lives inside the Overview <form>; Mantine
          // Button has no default type, so it would otherwise submit that form
          <Button
            type="button"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => {
              setRootError(null);
              setModalState({ mode: 'create' });
            }}
          >
            Add Schedule
          </Button>
        )}
      </Group>

      {pendingSave ? (
        <Text size="sm" c="dimmed">
          Save changes to activate scheduled recording, then manage recording times here.
        </Text>
      ) : (
        <Stack gap="md">
          {switchingAway && (
            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              Switching modes stops scheduled recording once saved. Your schedules are kept and
              restored if you switch back.
            </Alert>
          )}

          <Select
            label="Schedule Timezone"
            description={
              hasRecordSchedules
                ? 'Timezone can only be changed when no schedules exist. Delete all schedules to change it.'
                : `All schedule times are in ${zoneId}`
            }
            data={zoneOptions}
            value={zoneId}
            onChange={handleZoneChange}
            searchable
            allowDeselect={false}
            disabled={hasRecordSchedules || updateStream.isPending}
            maw={360}
          />

          {active.length === 0 && expired.length === 0 ? (
            <Stack gap="sm" align="flex-start">
              <Text size="sm" c="dimmed">
                No recording schedules yet — the stream will not record until you add one.
              </Text>
            </Stack>
          ) : (
            <>
              {active.length === 0 && (
                <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
                  No upcoming schedules — nothing will be recorded.
                </Alert>
              )}

              <Stack gap="xs">
                {active.map((s) => (
                  <ScheduleListItem
                    key={s.schedule.scheduleId}
                    scheduleAll={s}
                    zoneId={zoneId}
                    nowSecs={nowSecs}
                    onEdit={(original) => {
                      setRootError(null);
                      setModalState({ mode: 'edit', original });
                    }}
                    onDelete={handleDelete}
                  />
                ))}
              </Stack>

              {expired.length > 0 && (
                <Box>
                  <Button
                    type="button"
                    variant="subtle"
                    color="gray"
                    size="compact-sm"
                    leftSection={expiredOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                    onClick={toggleExpired}
                  >
                    Expired ({expired.length})
                  </Button>
                  <Collapse in={expiredOpen}>
                    <Stack gap="xs" mt="xs">
                      {expired.map((s) => (
                        <ScheduleListItem
                          key={s.schedule.scheduleId}
                          scheduleAll={s}
                          zoneId={zoneId}
                          nowSecs={nowSecs}
                          onDelete={handleDelete}
                          dimmed
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}
            </>
          )}
        </Stack>
      )}

      <ScheduleEditorModal
        opened={modalState.mode === 'create' || modalState.mode === 'edit'}
        mode={modalState.mode === 'edit' ? 'edit' : 'create'}
        defaultValues={modalDefaults}
        zoneId={zoneId}
        nowSecs={modalNowSecs}
        onSubmit={handleModalSubmit}
        onCancel={closeModal}
        isPending={updateStream.isPending}
        rootError={rootError}
      />
    </Card>
  );
}
